'use client';

import { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  BodyDefault,
  Button,
  HeadingItem,
  IconButton,
  LegacyDialog,
  MetaDefault,
  SegmentGroupHorizontal,
  Spinner,
} from '@terra/ui-design-system';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { Message01Icon, RefreshIcon, Cancel01Icon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { useChatAgentMessages } from '../../../../hooks/api/useChatAgentMessages';
import { useChatContextView } from '../../../../hooks/api/useChatContextView';
import {
  ContextViewBody,
  ContextViewHeaderStats,
} from '../context-view/ContextViewSection';
import { AgentTraceDetail } from './AgentTraceDetail';
import { AgentTraceTree } from './AgentTraceTree';
import {
  buildAgentTraceTree,
  buildParentMap,
  flattenAgentTraceTree,
  getLastSelectableNodeId,
} from './buildAgentTraceTree';

type AgentContextViewMode = 'trace' | 'context';

export function ChatAgentMessagesViewer({
  chatId,
  targetId,
  isOpen,
  isStreaming = false,
  onClose,
}: {
  chatId: string;
  targetId?: string;
  isOpen: boolean;
  isStreaming?: boolean;
  onClose: () => void;
}) {
  const tCommon = useTranslations('common');
  const tExecutions = useTranslations('chat.copilot.executions');
  const tAgentContext = useTranslations('chat.copilot.agentContext');
  const canLoadAgentMessages = Boolean(targetId);
  const [viewMode, setViewMode] = useState<AgentContextViewMode>('trace');
  const { data, isLoading, isError, refetch, isFetching } =
    useChatAgentMessages({
      chatId,
      applicationId: targetId,
      enabled: isOpen,
      refetchWhileStreaming: isOpen && isStreaming,
    });
  const {
    data: contextView,
    isLoading: isContextViewLoading,
    isError: isContextViewError,
    refetch: refetchContextView,
    isFetching: isContextViewFetching,
  } = useChatContextView({
    chatId,
    applicationId: targetId,
    enabled: isOpen,
    refetchWhileStreaming: isOpen && isStreaming,
  });

  const trace = useMemo(
    () => (data ? buildAgentTraceTree(data) : null),
    [data],
  );

  const nodesById = useMemo(() => {
    if (!trace) {
      return new Map<
        string,
        ReturnType<typeof flattenAgentTraceTree>[number]
      >();
    }

    return new Map(
      flattenAgentTraceTree(trace.root).map((node) => [node.id, node]),
    );
  }, [trace]);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!trace) {
      return;
    }

    const parents = buildParentMap(trace.root);
    const lastId = getLastSelectableNodeId(trace.root);

    const pathExpanded = new Set<string>();
    let cursor: string | null | undefined = lastId;

    while (cursor) {
      pathExpanded.add(cursor);
      cursor = parents.get(cursor) ?? null;
    }

    setExpandedIds((current) => {
      if (current.size === 0) {
        return pathExpanded;
      }

      const merged = new Set(current);

      for (const id of pathExpanded) {
        merged.add(id);
      }

      return merged;
    });

    setSelectedId((current) =>
      current && nodesById.has(current) ? current : lastId,
    );
  }, [trace, nodesById]);

  const selectedNode = selectedId ? (nodesById.get(selectedId) ?? null) : null;

  const toggleExpand = (id: string) => {
    setExpandedIds((current) => {
      const next = new Set(current);

      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }

      return next;
    });
  };

  return (
    <LegacyDialog.Root
      open={isOpen}
      onOpenChange={({ open }) => !open && onClose()}
    >
      <LegacyDialog.Backdrop />
      <LegacyDialog.Positioner>
        <LegacyDialog.Content
          maxW="90vw"
          w="90vw"
          h="85vh"
          display="flex"
          flexDirection="column"
          p={0}
          overflow="hidden"
        >
          <HStack
            px={4}
            py={3}
            borderBottomWidth="1px"
            borderColor="border.subtle"
            justifyContent="space-between"
            alignItems="center"
            bg="bg.default"
          >
            <HStack gap={3} alignItems="center">
              <Box
                p={2}
                borderRadius="md"
                bg="bg.subtle"
                color="fg.muted"
                display="flex"
              >
                <Message01Icon size={16} />
              </Box>
              <VStack alignItems="flex-start" gap={0.5}>
                <HeadingItem color="fg.default">
                  {tAgentContext('title')}
                </HeadingItem>
                <MetaDefault color="fg.muted">
                  {viewMode === 'context'
                    ? tAgentContext('liveModelView')
                    : data?.source === 'model_invocation'
                      ? tAgentContext('exactPayload')
                      : tAgentContext('checkpointFallback')}
                </MetaDefault>
              </VStack>
              <SegmentGroupHorizontal
                options={[
                  { value: 'trace', label: tAgentContext('trace') },
                  {
                    value: 'context',
                    label: tAgentContext('contextView'),
                  },
                ]}
                value={viewMode}
                onValueChange={({ value }) => {
                  if (value === 'trace' || value === 'context') {
                    setViewMode(value);
                  }
                }}
              />
            </HStack>
            <HStack gap={2} alignItems="center">
              {contextView && <ContextViewHeaderStats data={contextView} />}
              {trace?.degraded && (
                <Badge
                  variant="outline"
                  size="sm"
                  color="var(--colors-orange-9)"
                  borderColor="var(--colors-orange-9)"
                >
                  {tAgentContext('degradedView')}
                </Badge>
              )}
              {data && (
                <MetaDefault color="fg.muted" fontFamily="mono">
                  {data.messageCount} msgs
                  {data.invocationSequence
                    ? ` · #${data.invocationSequence}`
                    : ''}
                  {data.tools && data.tools.length > 0
                    ? ` · ${data.tools.length} tools`
                    : ''}
                </MetaDefault>
              )}
              <Button
                type="button"
                variant="ghost"
                size="xs"
                onClick={() => {
                  void refetch();
                  void refetchContextView();
                }}
                disabled={
                  !canLoadAgentMessages || isFetching || isContextViewFetching
                }
              >
                <RefreshIcon size={14} />
                {tCommon('refresh')}
              </Button>
              <IconButton
                type="button"
                variant="ghost"
                size="xs"
                aria-label={tAgentContext('closeAriaLabel')}
                onClick={onClose}
              >
                <Cancel01Icon size={16} />
              </IconButton>
            </HStack>
          </HStack>

          {!canLoadAgentMessages ? (
            <HStack flex="1" justifyContent="center" alignItems="center" px={6}>
              <BodyDefault color="fg.muted" textAlign="center">
                {tExecutions('noApplication')}
              </BodyDefault>
            </HStack>
          ) : viewMode === 'context' ? (
            isContextViewLoading ? (
              <HStack flex="1" justifyContent="center" alignItems="center">
                <Spinner size="sm" />
              </HStack>
            ) : isContextViewError || !contextView ? (
              <VStack
                flex="1"
                justifyContent="center"
                alignItems="center"
                gap={2}
              >
                <BodyDefault color="fg.error">
                  {tAgentContext('failedToLoadContextView')}
                </BodyDefault>
                <Button
                  type="button"
                  variant="outline"
                  size="xs"
                  onClick={() => {
                    void refetchContextView();
                  }}
                >
                  {tCommon('retry')}
                </Button>
              </VStack>
            ) : (
              <ContextViewBody data={contextView} />
            )
          ) : isLoading ? (
            <HStack flex="1" justifyContent="center" alignItems="center">
              <Spinner size="sm" />
            </HStack>
          ) : isError || !data || !trace ? (
            <VStack
              flex="1"
              justifyContent="center"
              alignItems="center"
              gap={2}
            >
              <BodyDefault color="fg.error">
                {tAgentContext('failedToLoadAgentMessages')}
              </BodyDefault>
              <Button
                type="button"
                variant="outline"
                size="xs"
                onClick={() => {
                  void refetch();
                }}
              >
                {tCommon('retry')}
              </Button>
            </VStack>
          ) : (
            <HStack flex="1" alignItems="stretch" minH={0}>
              <Box
                w="38%"
                minW="320px"
                maxW="460px"
                borderRightWidth="1px"
                borderColor="border.subtle"
                overflow="auto"
                bg="bg.default"
              >
                {trace.root.children.length === 0 ? (
                  <Box px={4} py={6}>
                    <BodyDefault color="fg.muted">
                      {tAgentContext('noMessagesYet')}
                    </BodyDefault>
                  </Box>
                ) : (
                  <AgentTraceTree
                    root={trace.root}
                    selectedId={selectedId}
                    expandedIds={expandedIds}
                    onSelect={setSelectedId}
                    onToggleExpand={toggleExpand}
                  />
                )}
              </Box>

              <VStack flex="1" minW={0} alignItems="stretch" overflow="hidden">
                <AgentTraceDetail node={selectedNode} />
              </VStack>
            </HStack>
          )}
        </LegacyDialog.Content>
      </LegacyDialog.Positioner>
    </LegacyDialog.Root>
  );
}
